import { action, autorun, computed, observable, reaction, runInAction } from 'mobx';
import SsoClient from '@teliaee/telia-sso-client';
import config from '../config';
import LanguageStore, { Language, languageSlug } from './LanguageStore';
import RootStore from './RootStore';
import RouteStore from './RouteStore';

const AUTH_TYPE_DEALER = 'dealerService';

interface AuthNumberUser {
  phoneNumber: string;
  productId: number;
}

export interface AuthPerson {
  displayName: string;
  firstName: string;
  lastName: string;
  personalCode?: string;
  personalCodeCountryCode?: string;
  profileId: string;
  profileAlias: string[];
}

export interface AuthCustomer {
  customerId: number;
  displayName: string;
  type: string;
  registrationNumber: string;
  registrationNumberCountryCode: string;
}

export interface AuthDealer {
  userName: string;
  displayName: string;
  title: string;
  department: string;
  roles: string[];
}

export interface Auth {
  authType: string;
  authLevel: number;
  person: AuthPerson;
  customer: AuthCustomer;
  dealer: AuthDealer;
  numberUser?: AuthNumberUser;
}

export interface SsoResultModel {
  message: string;
  action: string;
  auth: Auth;
  token: string;
  am: string;
  warning: string;
  error: string;
}

class SsoStore {
  @observable accessor selfServiceClicked: boolean = false;
  @observable accessor hardAuthRequired = false;
  @observable accessor auth: Auth;
  @observable accessor src: string = null;
  @observable accessor errors: string[] = [];
  @observable accessor initialSsoMessageReceived: boolean = false;
  @observable private accessor isSsoModalOpen = false;

  public ssoClient: SsoClient;
  private languageStore: LanguageStore;
  private routeStore: RouteStore;
  private app: App;

  private redirectUrl: string;
  private loginCallback: () => void;
  private redirectIfDealer: boolean = false;

  public constructor(rootStore: RootStore) {
    this.languageStore = rootStore.languageStore;
    this.routeStore = rootStore.routeStore;
    this.app = rootStore.app;

    runInAction(() => {
      this.ssoClient = SsoClient.connect(
        {
          onLogout: this.handleLogout,
          onLogin: this.handleLogin,
          registerCookies: true,
          env: config.environment as 'dev' | 'test' | 'live'
        },
        {
          locale: this.languageStore.currentLanguage,
          app: this.app,
          autoSelectSelf: true,
          hideB2B: true
        }
      );
    });

    this.ssoClient.loadInitialState();
    this.setIframeUrl();

    reaction(
      () => this.languageStore.currentLanguage,
      (locale: Language) => this.ssoClient.setOptions({ locale })
    );

    autorun(() => (window.userLoggedIn = this.loggedIn));
  }

  @computed
  get ssoLoaded() {
    return this.initialSsoMessageReceived;
  }

  @computed
  get loggedIn() {
    if (this.app === 'super') {
      return !!this.auth?.authLevel;
    }
    return !!this.auth && (!!this.auth.person || !!this.auth.dealer);
  }

  @computed
  get loggedInWithHardAuth() {
    return this.loggedIn && this.auth.authLevel > 1;
  }

  @computed
  get loginLevelSufficient() {
    return this.hardAuthRequired ? this.loggedInWithHardAuth : this.loggedIn;
  }

  @computed
  get person() {
    return this.auth && this.auth.person;
  }

  @computed
  get customer() {
    return this.auth && this.auth.customer;
  }

  @computed
  get dealer() {
    return this.auth && this.auth.dealer;
  }

  @computed
  get numberUser() {
    return this.auth && this.auth.numberUser;
  }

  @computed
  get ssoFrameVisible() {
    return this.ssoClient && this.isSsoModalOpen;
  }

  @action
  public openSsoFrame() {
    this.isSsoModalOpen = true;
  }

  @computed
  get customerId() {
    return this.customer && this.customer.customerId;
  }

  @computed
  get isDealer() {
    return this.auth && this.auth.authType === AUTH_TYPE_DEALER;
  }

  @computed
  get personalCode() {
    return this.person && this.person.personalCode;
  }

  public ensureLogin = (redirectUrl?: string) => {
    if (!this.loginLevelSufficient) {
      this.setLoginParameters(redirectUrl);
    } else if (redirectUrl) {
      this.routeStore.replace(redirectUrl);
    }
  };

  public ensureHardLogin = (redirectUrl?: string) => {
    runInAction(() => {
      this.hardAuthRequired = true;
      this.ssoClient.setOptions({ authLevel: 2 });
    });
    this.ensureLogin(redirectUrl);
  };

  public ensureLoginOrDealerWithCallback = (callback: () => void) => {
    if (!this.loginLevelSufficient && !this.isDealer) {
      this.setLoginParameters(null, callback);
    } else if (callback) {
      callback();
    }
  };

  private handleLogin = (ssoResult: SsoResultModel) => {
    runInAction(() => {
      this.initialSsoMessageReceived = true;

      if (!this.selfServiceClicked && this.closeSsoFrameOnPath()) {
        this.closeSsoFrame();
      }

      this.auth = ssoResult.auth;

      if (this.redirectIfDealer && this.isDealer) {
        this.routeStore.replace(config[this.app].selfServiceUrl + languageSlug(this.languageStore.currentLanguage));
      }

      if (this.loggedIn && this.redirectUrl && !this.dealer) {
        const url = this.redirectUrl;
        this.redirectUrl = null;
        this.routeStore.replace(url);
      }

      if (this.loggedIn) {
        this.closeSsoFrame();
      }

      if (this.loggedIn && this.loginCallback && typeof this.loginCallback === 'function') {
        this.loginCallback();
        this.loginCallback = null;
      }

      this.ssoClient.setOptions({ selectMandate: true });
      this.redirectIfDealer = false;
    });
  };

  public resetLoginRequired = () => {
    runInAction(() => {
      this.hardAuthRequired = false;
      this.ssoClient.setOptions({ authLevel: 0 });
    });
    this.setIframeUrl();
  };

  private closeSsoFrameOnPath = () => {
    return (!this.routeStore.pathOnlyIncludesSegment() && !this.redirectUrl) || this.loggedIn;
  };

  public getLoginUrl = (goto?: string) => {
    return this.ssoClient.getLoginUrl({ goto });
  };

  private setIframeUrl = () => {
    runInAction(() => (this.src = this.ssoClient.getLoginUrl({ selectMandate: true })));
  };

  public handleLogout = () => {
    runInAction(() => {
      this.initialSsoMessageReceived = true;
      this.auth = null;
      this.redirectUrl = '';
      this.routeStore.replace(this.redirectUrl);
      this.isSsoModalOpen = false;
    });
  };

  public doLogout = () => {
    runInAction(() => this.ssoClient.doLogout());
  };

  public setLoginParameters = (redirectUrl?: string, loginCallback?: () => void) => {
    runInAction(() => {
      this.setIframeUrl();
      this.openSsoFrame();
      this.redirectUrl = redirectUrl;
      this.loginCallback = loginCallback;
      this.redirectIfDealer = false;
    });
  };

  public closeSsoFrame = () => {
    runInAction(() => {
      this.hardAuthRequired = false;
      this.ssoClient.setOptions({ authLevel: 0 });
      this.isSsoModalOpen = false;
      this.setIframeUrl();
      this.redirectIfDealer = false;
    });
  };
}

export default SsoStore;
